import * as yup from 'yup'
import {
  REMOTE_CONFIG_DESCRIPTION_MAX_LENGTH,
  REMOTE_CONFIG_KEY_MAX_LENGTH,
  REMOTE_CONFIG_KEY_PATTERN,
  REMOTE_CONFIG_VALUE_MAX_BYTES,
  REMOTE_CONFIG_VALUE_TOO_LARGE_MESSAGE,
} from 'src/constants/remote-config/remoteConfig'
import type {
  RemoteConfig,
  RemoteConfigFormMode,
  RemoteConfigFormValues,
} from 'src/interfaces/remoteConfig'
import { buildRemoteConfigKeyTakenMessage } from 'src/utils/remoteConfig/remoteConfigErrors'
import {
  getRemoteConfigValueBytes,
  toRemoteConfigValue,
  validateRemoteConfigValueText,
} from 'src/utils/remoteConfig/remoteConfigValue'

interface RemoteConfigFormValidatorOptions {
  mode: RemoteConfigFormMode
  existingRemoteConfigs: RemoteConfig[]
}

export const buildRemoteConfigFormValidator = ({
  mode,
  existingRemoteConfigs,
}: RemoteConfigFormValidatorOptions) => {
  // Key, app and type are fixed after creation, so only a new key is checked.
  const keySchema =
    mode === 'create'
      ? yup
          .string()
          .trim()
          .required('Enter a key')
          .matches(
            REMOTE_CONFIG_KEY_PATTERN,
            'Use lowercase letters, digits, dots and underscores only',
          )
          .max(REMOTE_CONFIG_KEY_MAX_LENGTH, 'Keep the key to 100 characters')
          .test('keyIsUnique', '', function (value) {
            const key = (value ?? '').trim()
            const { app } = this.parent as RemoteConfigFormValues
            const isTaken = existingRemoteConfigs.some((remoteConfig) => {
              return remoteConfig.app === app && remoteConfig.key === key
            })

            if (!isTaken) {
              return true
            }

            return this.createError({ message: buildRemoteConfigKeyTakenMessage(app, key) })
          })
      : yup.string()

  return yup.object({
    key: keySchema,
    description: yup
      .string()
      .max(REMOTE_CONFIG_DESCRIPTION_MAX_LENGTH, 'Keep the description to 500 characters'),
    valueText: yup.string().test('valueIsValid', '', function (value) {
      const formValues = this.parent as RemoteConfigFormValues
      const valueText = value ?? ''
      const message = validateRemoteConfigValueText(formValues.type, valueText)

      if (message) {
        return this.createError({ message })
      }

      if (
        getRemoteConfigValueBytes(toRemoteConfigValue({ ...formValues, valueText })) >
        REMOTE_CONFIG_VALUE_MAX_BYTES
      ) {
        return this.createError({ message: REMOTE_CONFIG_VALUE_TOO_LARGE_MESSAGE })
      }

      return true
    }),
  })
}
